import { useEffect, useRef, useState } from "react";

const ICE_SERVERS = [{ urls: "stun:stun.l.google.com:19302" }];

/**
 * Free, peer-to-peer (mesh) voice chat for a Tap-First room — no paid relay
 * service, no server-side media. Only does anything when `enabled` (the
 * room's voice_enabled setting) is true.
 *
 * Every connected participant opens a direct RTCPeerConnection to every
 * other connected participant (audio only). Muting is instant and entirely
 * signaling-free: it just flips the local track's `.enabled` flag based on
 * `micShouldBeOpen` — no renegotiation, nothing sent over the wire. The
 * caller decides what that flag should be (host always on; buzzer winner's
 * mic on until a point is awarded, the buzzer resets, or the timer expires).
 *
 * Signaling (SDP offers/answers + ICE candidates) rides the existing
 * Socket.IO connection via a single "voice_signal" event the server just
 * relays — it never inspects the payload.
 *
 * Known limitation: STUN-only, no TURN relay. Works for the vast majority of
 * home/office networks; a device behind a strict/symmetric NAT may fail to
 * connect peer-to-peer. That's the trade-off for keeping this free.
 */
export function useVoiceChat({ enabled, socket, connected, roomCode, micShouldBeOpen, participants }) {
  const [remoteStreams, setRemoteStreams] = useState({}); // { sid: MediaStream }
  const [micError, setMicError] = useState(null);
  const [localReady, setLocalReady] = useState(false);
  const localStreamRef = useRef(null);
  const peersRef = useRef({}); // { sid: RTCPeerConnection }
  const apiRef = useRef({});

  // The entire mute state machine: flip the outgoing track's enabled flag.
  // No renegotiation, no signaling — the remote side just hears silence.
  useEffect(() => {
    const stream = localStreamRef.current;
    if (stream) stream.getAudioTracks().forEach((t) => { t.enabled = !!micShouldBeOpen; });
  }, [micShouldBeOpen, localReady]);

  // Main lifecycle: acquire the mic, wire up signaling, tear everything down
  // on room leave / voice being turned off / disconnect.
  useEffect(() => {
    if (!enabled || !socket || !connected || !roomCode) return;
    let cancelled = false;

    const sendSignal = (to, type, payload) =>
      socket.emit("voice_signal", { room_code: roomCode, to, type, payload });

    const closePeer = (sid) => {
      const pc = peersRef.current[sid];
      if (pc) {
        try { pc.close(); } catch (_) { /* no-op */ }
        delete peersRef.current[sid];
      }
      setRemoteStreams((prev) => {
        if (!(sid in prev)) return prev;
        const next = { ...prev };
        delete next[sid];
        return next;
      });
    };

    const ensurePeer = (sid) => {
      if (peersRef.current[sid]) return peersRef.current[sid];
      const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
      peersRef.current[sid] = pc;
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((t) => pc.addTrack(t, localStreamRef.current));
      }
      pc.onicecandidate = (e) => {
        if (e.candidate) sendSignal(sid, "ice", e.candidate);
      };
      pc.ontrack = (e) => {
        setRemoteStreams((prev) => ({ ...prev, [sid]: e.streams[0] }));
      };
      return pc;
    };

    const startConnectionTo = async (sid) => {
      const pc = ensurePeer(sid);
      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        sendSignal(sid, "offer", offer);
      } catch (e) {
        console.warn("[voice] failed to create offer for", sid, e);
      }
    };

    const handleSignal = async ({ from, type, payload }) => {
      if (!from || from === socket.id) return;
      const pc = ensurePeer(from);
      try {
        if (type === "offer") {
          await pc.setRemoteDescription(payload);
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          sendSignal(from, "answer", answer);
        } else if (type === "answer") {
          await pc.setRemoteDescription(payload);
        } else if (type === "ice" && payload) {
          await pc.addIceCandidate(payload).catch(() => {});
        }
      } catch (e) {
        console.warn("[voice] failed to handle signal", type, e);
      }
    };

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        stream.getAudioTracks().forEach((t) => { t.enabled = !!micShouldBeOpen; });
        localStreamRef.current = stream;
        setMicError(null);
        apiRef.current = { startConnectionTo, closePeer, mySid: socket.id };
        socket.on("voice_signal", handleSignal);
        setLocalReady(true);
      } catch (e) {
        setMicError("Mic permission was blocked — voice chat is off for you, but you can still play.");
      }
    })();

    return () => {
      cancelled = true;
      socket.off("voice_signal", handleSignal);
      Object.keys(peersRef.current).forEach(closePeer);
      apiRef.current = {};
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((t) => t.stop());
        localStreamRef.current = null;
      }
      setRemoteStreams({});
      setLocalReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, socket, connected, roomCode]);

  // Reconcile peer connections whenever the room roster changes. Deterministic
  // glare avoidance: of any two peers, the one with the lower socket id always
  // initiates the offer — both sides agree on this without any negotiation.
  useEffect(() => {
    if (!enabled || !connected || !localReady) return;
    const { startConnectionTo, closePeer, mySid } = apiRef.current;
    if (!startConnectionTo) return;
    const liveSids = new Set(
      (participants || []).map((p) => p.socket_id).filter((sid) => sid && sid !== mySid)
    );
    liveSids.forEach((sid) => {
      if (!peersRef.current[sid] && mySid < sid) startConnectionTo(sid);
    });
    Object.keys(peersRef.current).forEach((sid) => {
      if (!liveSids.has(sid)) closePeer(sid);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, connected, localReady, participants]);

  return { remoteStreams, micError };
}
